import { create } from "zustand";
import { cancelMySubscription, getMySubscription, } from "@/shared/api/subscriptions";
export function createSubscriptionStore(api) {
    // Один запрос на пользователя: витрина и блок «Моя подписка» монтируются
    // одновременно и не должны слать два одинаковых GET.
    let inFlight = null;
    // Отмена в пути — общий промис для всех, кто успел нажать.
    let cancelInFlight = null;
    // Поколение данных. Растёт, когда состояние поменялось «в обход» загрузки
    // (отмена, смена пользователя): ответ GET, ушедшего РАНЬШЕ этого
    // момента, устарел и применять его нельзя — иначе поздний ответ вернул бы
    // «активна» поверх только что отменённой подписки.
    let generation = 0;
    return create((set, get) => ({
        subscription: null,
        status: "idle",
        userId: null,
        resolved: false,
        isCancelling: false,
        load: (userId, options) => {
            if (inFlight?.userId === userId)
                return inFlight.promise;
            const state = get();
            if (!options?.force && state.userId === userId && state.status === "ready") {
                return Promise.resolve();
            }
            // Сменился пользователь — чужую подписку не держим ни кадра.
            if (state.userId !== userId) {
                generation += 1;
                set({ subscription: null, userId, resolved: false });
            }
            set({ status: "loading" });
            const startedAt = generation;
            // Ответ применяем, только если он всё ещё про этого пользователя и с
            // момента запроса состояние не меняли в обход загрузки.
            const isCurrent = () => get().userId === userId && generation === startedAt;
            const promise = api
                .getMySubscription()
                .then((subscription) => {
                if (!isCurrent())
                    return;
                set({ subscription, status: "ready", resolved: true });
            })
                .catch((error) => {
                if (!isCurrent())
                    return;
                // Любая ошибка — ТИХО, без тоста: ручки может ещё не быть (фронт
                // выкатили раньше бэка → 404), сеть моргнула, 5xx, 401/403. Считаем,
                // что подписки нет: блок «Моя подписка» не показываем, витрина
                // работает как раньше.
                console.error("Ошибка загрузки подписки:", error);
                set({ status: "error", resolved: true });
            })
                .finally(() => {
                if (inFlight?.promise === promise)
                    inFlight = null;
            });
            inFlight = { userId, promise };
            return promise;
        },
        cancel: () => {
            // Двойной клик не должен слать второй запрос: повторный вызов получает
            // тот же промис и тот же исход (а не ложный «успех», пока первый в пути).
            if (cancelInFlight)
                return cancelInFlight;
            const userId = get().userId;
            set({ isCancelling: true });
            const promise = api
                .cancelMySubscription()
                .then((subscription) => {
                if (get().userId === userId) {
                    // Загрузки, начатые до этого момента, несут состояние ДО отмены.
                    generation += 1;
                    set({ subscription, status: "ready", resolved: true });
                }
                return subscription;
            })
                .finally(() => {
                cancelInFlight = null;
                set({ isCancelling: false });
            });
            cancelInFlight = promise;
            return promise;
        },
        reset: () => {
            inFlight = null;
            // Ответ в пути отсечётся сам: он про пользователя, которого в сторе уже нет.
            set({
                subscription: null,
                status: "idle",
                userId: null,
                resolved: false,
                isCancelling: false,
            });
        },
    }));
}
export const useSubscriptionStore = createSubscriptionStore({
    getMySubscription,
    cancelMySubscription,
});
